import { type BaseChainflipAsset } from '@chainflip/utils/chainflip';
import { formatTimestampShort } from '@chainflip/utils/date';
import { formatUsdValue } from '@chainflip/utils/number';
import { abbreviate } from '@chainflip/utils/string';
import { MAX_TICK, rateToTick } from '@chainflip/utils/tickMath';
import classNames from 'classnames';
import { Link, TokenWithChainByAsset, Tooltip } from '@/shared/components';
import { DistanceIcon, MinusCircleIcon, PlusCircleIcon } from '@/shared/icons/small';
import { chainflipAssetMap, TokenAmount } from '@/shared/utils';
import { getFormattedTickRate } from '@/shared/utils/tickMath';
import { RangerOrderStatus } from './OrderStatus';
import Pill from '../flip-ui-kit/Pill';

export const BuyPill = () => <Pill color="green" text="Buy" Icon={PlusCircleIcon} />;

export const SellPill = () => <Pill color="red" text="Sell" Icon={MinusCircleIcon} />;

export const OrderTypePill = ({ type }: { type: 'LIMIT' | 'RANGE' }) => (
  <Pill color="neutral" text={type === 'LIMIT' ? 'Limit' : 'Range'} />
);

export const OrderPrice = ({
  tick,
  baseAsset,
}: {
  tick: number | string;
  baseAsset: BaseChainflipAsset;
}) => {
  const allDecimalsString = getFormattedTickRate(tick, baseAsset);
  const defaultDecimalsString = getFormattedTickRate(tick, baseAsset, 4);
  const exceedsDecimals = allDecimalsString.length > defaultDecimalsString.length;

  return (
    <div className="inline-block">
      <Tooltip
        content={exceedsDecimals ? allDecimalsString : defaultDecimalsString}
        pointer={false}
      >
        {defaultDecimalsString}
      </Tooltip>
    </div>
  );
};

type RangeOrderRowProps = {
  valueUsd?: string | null;
  baseAsset: BaseChainflipAsset;
  lpAccount: string | null;
  creationTimestamp?: string;
  displayCreationTimestamp?: boolean;
  additionalCells?: React.ReactNode;
  onClick?: (asset: BaseChainflipAsset) => void;
  isMobileView?: boolean;
  baseAmount: bigint | undefined;
  quoteAmount: bigint | undefined;
  baseFeesEarned: bigint;
  quoteFeesEarned: bigint;
  lowerTick: number;
  upperTick: number;
  isRpcOrderOpen: boolean;
  currentRangeOrderPrice: number;
};

type RangeOrderRowMobileProps = Omit<
  RangeOrderRowProps,
  | 'onClick'
  | 'valueUsd'
  | 'lpAccount'
  | 'additionalCells'
  | 'baseAmount'
  | 'quoteAmount'
  | 'baseFeesEarned'
  | 'quoteFeesEarned'
  | 'currentRangeOrderPrice'
> & {
  isInRange: boolean;
  baseTokenAmount: TokenAmount | null;
  earnedFeesBaseAsset: TokenAmount;
  quoteTokenAmount: TokenAmount | null;
  earnedFeesQuoteAsset: TokenAmount;
};

type RangeOrderRowDesktopProps = RangeOrderRowMobileProps &
  Pick<RangeOrderRowProps, 'onClick' | 'valueUsd' | 'lpAccount' | 'additionalCells'>;

const RangeOrderRowMobile = ({
  isRpcOrderOpen,
  isInRange,
  baseAsset,
  baseTokenAmount,
  earnedFeesBaseAsset,
  quoteTokenAmount,
  earnedFeesQuoteAsset,
  lowerTick,
  upperTick,
  displayCreationTimestamp = true,
  creationTimestamp,
}: RangeOrderRowMobileProps) => {
  const usdcSymbol = chainflipAssetMap.Usdc.symbol;

  return (
    <tr className="flex flex-col justify-center gap-y-2 p-3 font-aeonikMono text-12">
      <div className="flex items-center justify-between">
        <OrderTypePill type="RANGE" />
        <RangerOrderStatus isRpcOrderOpen={isRpcOrderOpen} isInRange={isInRange} />
      </div>
      <div className="grid w-full grid-cols-[auto,auto] content-start justify-between gap-1">
        <span className="flex gap-x-2 text-white">
          <TokenWithChainByAsset asset={baseAsset} /> {baseTokenAmount?.toFormatted()}{' '}
          {chainflipAssetMap[baseAsset].symbol}
        </span>
        <div
          className={
            earnedFeesBaseAsset.toBigInt() > 0 ? classNames('ml-auto text-cf-green-3') : ''
          }
        >
          {earnedFeesBaseAsset.toBigInt() > 0 && '+'}
          {earnedFeesBaseAsset?.toFormatted()} {chainflipAssetMap[baseAsset].symbol}
        </div>

        <span className="flex gap-x-2 text-white">
          <TokenWithChainByAsset asset="Usdc" /> {quoteTokenAmount?.toFormatted()}{' '}
          {chainflipAssetMap.Usdc.symbol}
        </span>
        <div
          className={
            earnedFeesQuoteAsset.toBigInt() > 0 ? classNames('ml-auto text-cf-green-3') : ''
          }
        >
          {earnedFeesQuoteAsset.toBigInt() > 0 && '+'}
          {earnedFeesQuoteAsset?.toFormatted()} {chainflipAssetMap.Usdc.symbol}
        </div>
      </div>
      <div className="flex items-center gap-x-2">
        <OrderPrice baseAsset={baseAsset} tick={lowerTick} /> {usdcSymbol}
        <DistanceIcon className="text-cf-light-1" />
        <OrderPrice baseAsset={baseAsset} tick={upperTick} /> {upperTick < MAX_TICK && usdcSymbol}
      </div>
      {displayCreationTimestamp && (
        <span>
          {creationTimestamp ? formatTimestampShort(creationTimestamp) : 'A few seconds ago'}
        </span>
      )}
    </tr>
  );
};

const RangeOrderRowDesktop = ({
  onClick,
  baseAsset,
  baseTokenAmount,
  quoteTokenAmount,
  valueUsd,
  lowerTick,
  upperTick,
  earnedFeesBaseAsset,
  earnedFeesQuoteAsset,
  lpAccount,
  isRpcOrderOpen,
  isInRange,
  displayCreationTimestamp = true,
  creationTimestamp,
  additionalCells,
}: RangeOrderRowDesktopProps) => (
  <tr
    className={classNames(
      onClick && 'transition:ease-in duration-150 hover:cursor-pointer hover:bg-cf-gray-3',
    )}
    onClick={(e) => {
      e.stopPropagation();
      onClick?.(baseAsset);
    }}
  >
    <td className="whitespace-nowrap">
      <OrderTypePill type="RANGE" />
    </td>
    <td className="whitespace-nowrap text-12">
      <div className="flex flex-col gap-y-2 font-aeonikMono text-cf-light-3">
        <span className="flex gap-x-2">
          <TokenWithChainByAsset asset={baseAsset} /> {baseTokenAmount?.toFormatted()}{' '}
          {chainflipAssetMap[baseAsset].symbol}
        </span>
        <span className="flex gap-x-2">
          <TokenWithChainByAsset asset="Usdc" /> {quoteTokenAmount?.toFormatted()}{' '}
          {chainflipAssetMap.Usdc.symbol}
        </span>
      </div>
    </td>
    {valueUsd && (
      <td className="whitespace-nowrap font-aeonikMono text-12">{formatUsdValue(valueUsd)}</td>
    )}
    <td className="flex whitespace-nowrap font-aeonikMono text-12">
      <div className="flex flex-col items-center ">
        <OrderPrice baseAsset={baseAsset} tick={lowerTick} />
        <DistanceIcon className="text-cf-light-1" />
        <OrderPrice baseAsset={baseAsset} tick={upperTick} />
      </div>
    </td>
    <td className="whitespace-nowrap font-aeonikMono text-12">
      <div className="flex flex-col space-y-1">
        <div className={earnedFeesBaseAsset.toBigInt() > 0 ? classNames('text-cf-green-3') : ''}>
          {earnedFeesBaseAsset.toBigInt() > 0 && '+'}
          {earnedFeesBaseAsset?.toFormatted()} {chainflipAssetMap[baseAsset].symbol}
        </div>
        <div className={earnedFeesQuoteAsset.toBigInt() > 0 ? classNames('text-cf-green-3') : ''}>
          {earnedFeesQuoteAsset.toBigInt() > 0 && '+'}
          {earnedFeesQuoteAsset?.toFormatted()} {chainflipAssetMap.Usdc.symbol}
        </div>
      </div>
    </td>
    {lpAccount && (
      <td className="whitespace-nowrap font-aeonikMono text-12">
        <Link href={`/lps/${lpAccount}`} noPropagate underline>
          <span>{abbreviate(lpAccount)}</span>
        </Link>
      </td>
    )}
    <td className="whitespace-nowrap font-aeonikMono text-12">
      <RangerOrderStatus isRpcOrderOpen={isRpcOrderOpen} isInRange={isInRange} />
    </td>
    {displayCreationTimestamp && (
      <td className="whitespace-nowrap font-aeonikMono text-12">
        {creationTimestamp ? formatTimestampShort(creationTimestamp) : 'A few seconds ago'}
      </td>
    )}
    {additionalCells}
  </tr>
);

export const RangeOrderRow = (props: RangeOrderRowProps) => {
  const baseTokenAmount = TokenAmount.fromAsset(props.baseAmount, props.baseAsset);
  const quoteTokenAmount = TokenAmount.fromAsset(props.quoteAmount, 'Usdc');
  const earnedFeesBaseAsset = TokenAmount.fromAsset(props.baseFeesEarned, props.baseAsset);
  const earnedFeesQuoteAsset = TokenAmount.fromAsset(props.quoteFeesEarned, 'Usdc');

  const currentTick = rateToTick(props.currentRangeOrderPrice, props.baseAsset);
  const isInRange = currentTick >= props.lowerTick && currentTick <= props.upperTick;

  return props.isMobileView ? (
    <RangeOrderRowMobile
      {...props}
      isInRange={isInRange}
      baseTokenAmount={baseTokenAmount}
      earnedFeesBaseAsset={earnedFeesBaseAsset}
      quoteTokenAmount={quoteTokenAmount}
      earnedFeesQuoteAsset={earnedFeesQuoteAsset}
    />
  ) : (
    <RangeOrderRowDesktop
      {...props}
      isInRange={isInRange}
      baseTokenAmount={baseTokenAmount}
      earnedFeesBaseAsset={earnedFeesBaseAsset}
      quoteTokenAmount={quoteTokenAmount}
      earnedFeesQuoteAsset={earnedFeesQuoteAsset}
    />
  );
};
